import useImpersonate from '@State/Impersonate/useImpersonate';
import usePermissions from '@State/Permissions/usePermissions';
import React, { useContext, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLoadingState } from '../../hooks/useLoadingState';
import i18n from '../../i18n';
import { userCanView } from '../Header/userCanView';
import ChatToggleButton from './ChatToggleButton';
import { SidebarCtx } from './SidebarContext';
import { GroupTitle, NavGroup, NavItem, NavItemIcon, NavItemLink, NavList } from './styles';

import {
  IconBarChart,
  IconCalendarNav,
  IconCloud,
  IconDoughnutChart,
  IconFiles,
  IconRecycle,
  IconStaircase,
  IconUser,
  IconWorkplaces,
} from '../Icon';

export interface NavigationItem {
  title: string;
  path?: string;
  exact?: boolean;
  authority?: ('userAdmin' | 'clientAdmin')[];
  permission?: string;
  subNav?: SubNavItem[];
  description?: string;
  widgets?: React.JSX.Element[];
}

interface SubNavItem {
  title: string;
  path: string;
  description?: string;
  icon?: React.JSX.Element;
  widget?: React.JSX.Element;
  authority?: ('userAdmin' | 'clientAdmin')[];
  permission?: string;
  exact?: boolean;
}

const navigationListItems: NavigationItem[] = [
  {
    title: i18n.t('navigation_order_title'),
    subNav: [
      {
        title: i18n.t('navigation_services_title'),
        path: '/services',
        description: i18n.t('navigation_services_description'),
        icon: <IconRecycle $outlined size="24px" />,
      },
      {
        title: i18n.t('navigation_scheduled_title'),
        path: '/scheduled',
        description: i18n.t('navigation_scheduled_description'),
        icon: <IconCalendarNav $outlined size="24px" />,
      },
      {
        title: i18n.t('navigation_deviations_title'),
        path: '/deviations',
        description: i18n.t('navigation_deviations_description'),
        icon: <IconFiles $outlined size="24px" />,
      },
      {
        title: i18n.t('navigation_workplaces_title'),
        path: '/workplaces',
        description: i18n.t('navigation_workplaces_description'),
        icon: <IconWorkplaces $outlined size="24px" />,
        authority: ['clientAdmin'],
      },
    ],
  },
  {
    title: i18n.t('navigation_reports_title'),
    subNav: [
      {
        title: i18n.t('navigation_report_statistics_title'),
        path: '/reports/statistics',
        permission: 'SpreadsheetStatistics',
        description: i18n.t('navigation_report_statistics_description'),
        icon: <IconFiles $outlined size="24px" />,
      },
      {
        title: i18n.t('trends_title'),
        path: '/trends',
        permission: 'GraphMaterialsByMonth',
        description: i18n.t('navigation_trends_description'),
        icon: <IconBarChart $outlined size="24px" />,
      },
      {
        title: i18n.t('navigation_report_waste_title'),
        path: '/reports/waste',
        permission: 'SpreadsheetStatistics',
        description: i18n.t('navigation_report_waste_description'),
        icon: <IconFiles $outlined size="24px" />,
      },
      {
        title: i18n.t('navigation_sustainability_report_title'),
        path: '/sustainability-report',
        authority: ['clientAdmin', 'userAdmin'],
        description: i18n.t('navigation_sustainability_report_description'),
        icon: <IconFiles $outlined size="24px" />,
      },
    ],
  },
  {
    title: i18n.t('navigation_sustainability_title'),
    subNav: [
      {
        title: i18n.t('waste_hierarchy_title'),
        path: '/waste-hierarchy',
        description: i18n.t('navigation_waste_hierarchy_description'),
        icon: <IconStaircase $outlined size="24px" />,
      },
      {
        title: i18n.t('workplace_analysis_title'),
        path: '/workplace-analysis',
        description: i18n.t('navigation_workplace_analysis_description'),
        icon: <IconBarChart $outlined size="24px" />,
      },
      {
        title: i18n.t('scope3_title'),
        path: '/scope3',
        permission: 'SpreadsheetStatistics',
        description: i18n.t('navigation_scope3_description'),
        icon: <IconDoughnutChart $outlined size="24px" />,
      },
    ],
  },
  {
    title: i18n.t('navigation_estates_title'),
    permission: 'SpreadsheetEstatesStatistics',
    subNav: [
      {
        title: i18n.t('navigation_estates_report_title'),
        path: '/estates',
        description: i18n.t('navigation_estates_description'),
        icon: <IconFiles $outlined size="24px" />,
        exact: true,
      },
      {
        title: i18n.t('navigation_estates_waste_hierarchy_title'),
        path: '/estates/waste-hierarchy',
        description: i18n.t('navigation_estates_waste_hierarchy_description'),
        icon: <IconFiles $outlined size="24px" />,
      },
      {
        title: i18n.t('navigation_estates_scope3_title'),
        path: '/estates/scope3',
        description: i18n.t('navigation_estates_scope3_description'),
        icon: <IconDoughnutChart $outlined size="24px" />,
      },
    ],
  },
  {
    title: i18n.t('navigation_administration_title'),
    subNav: [
      {
        title: i18n.t('navigation_users_title'),
        path: '/users',
        authority: ['clientAdmin', 'userAdmin'],
        description: i18n.t('navigation_users_description'),
        icon: <IconUser $outlined size="24px" />,
      },
      {
        title: i18n.t('navigation_users_admin_title'),
        path: '/remondis-admins',
        authority: ['userAdmin'],
        description: i18n.t('user_admins_description'),
        icon: <IconUser $outlined size="24px" />,
      },
      {
        title: i18n.t('invoices_title'),
        path: '/invoices',
        authority: ['clientAdmin'],
        description: i18n.t('invoices_description'),
        icon: <IconFiles $outlined size="24px" />,
      },
      {
        title: i18n.t('api_client'),
        path: '/admin/api-clients',
        authority: ['clientAdmin'],
        description: i18n.t('navigation_api_clients_description'),
        icon: <IconCloud $outlined size="24px" />,
      },
    ],
  },
];

interface SidebarNavigationListProps {
  userAdmin: boolean;
  clientAdmin: boolean;
}

const SidebarNavigationList = ({ userAdmin, clientAdmin }: SidebarNavigationListProps) => {
  const { pathname } = useLocation();
  const { permissions, getPermissions } = usePermissions();
  const { handleNavigation } = useContext(SidebarCtx);
  const isLoading = useLoadingState();
  const { impersonate } = useImpersonate();

  useEffect(() => {
    if (impersonate) {
      getPermissions();
    }
  }, [impersonate, getPermissions]);

  const isSelected = (path: string, exact?: boolean) =>
    exact ? pathname === path : pathname.startsWith(path);

  function getParsedMenu(): NavigationItem[] {
    const filteredItems = navigationListItems
      .map((item) => {
        const copyItem = { ...item };
        if (item.subNav) {
          copyItem.subNav = item.subNav.filter((subNavItem) =>
            userCanView({ item: subNavItem, userAdmin, clientAdmin, permissions })
          );
          return copyItem.subNav.length > 0 &&
            userCanView({ item, userAdmin, clientAdmin, permissions })
            ? copyItem
            : undefined;
        }
        return userCanView({ item, userAdmin, clientAdmin, permissions }) ? copyItem : undefined;
      })
      .filter((item): item is NavigationItem => !!item);
    return filteredItems;
  }

  if (isLoading) {
    return null;
  }

  return (
    <>
      {getParsedMenu().map((navigation, groupIndex) => (
        <NavGroup key={`group-${groupIndex}`}>
          <GroupTitle>{navigation.title}</GroupTitle>
          <NavList>
            {navigation.subNav?.map((item, index) => (
              <NavItem key={`${item.path}-${index}`}>
                <NavItemLink
                  to={item.path}
                  $isActive={isSelected(item.path, item.exact)}
                  onClick={handleNavigation}
                >
                  {item.icon && <NavItemIcon>{item.icon}</NavItemIcon>}
                  {item.title}
                </NavItemLink>
              </NavItem>
            ))}
          </NavList>
        </NavGroup>
      ))}

      <NavGroup>
        <GroupTitle>{i18n.t('navigation_support_title')}</GroupTitle>
        <NavList>
          <ChatToggleButton />
        </NavList>
      </NavGroup>
    </>
  );
};

export default SidebarNavigationList;
